import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useId,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '@/lib/api';

// ===== utilidades de fecha (igual que antes)
const formatDate = (dateString: string | Date) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffInHours < 48) return 'Yesterday';
    const days = Math.floor(diffInHours / 24);
    if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    return date.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// ===== iconos
const notificationIcons = {
  info: <InfoOutlinedIcon color="info" />,
  warning: <WarningAmberIcon color="warning" />,
  success: <CheckCircleOutlineIcon color="success" />,
  error: <ErrorOutlineIcon color="error" />,
} as const;

export interface UINotification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  type: 'info' | 'warning' | 'success' | 'error';
  createdAt: string;
  link?: string;
  metadata?: Record<string, unknown>;
  expanded?: boolean;
}

export interface ApiNotification {
  id: string | number;
  title?: string;
  message: string;
  read: boolean;
  type?: string;
  createdAt?: string | Date;
  userId?: string;
  link?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationsModalProps {
  open: boolean;
  onClose: () => void;
  userId?: string | number;
  onNotificationClick?: (notification: UINotification) => void;
}

/* ---------- Type guards sin any ---------- */
type Stoppable = { stopPropagation: () => void };
function hasStopPropagation(e: unknown): e is Stoppable {
  return !!e && typeof e === 'object' && typeof (e as Record<string, unknown>).stopPropagation === 'function';
}
function isHTMLElement(el: unknown): el is HTMLElement {
  return typeof window !== 'undefined' && el instanceof HTMLElement;
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState<UINotification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const closingRef = useRef(false);              // evita dobles cierres
  const isMounted = useRef(true);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null); // foco previo al abrir

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const titleId = useId(); // para aria-labelledby

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Guarda el elemento que tenía el foco cuando se abre
  useEffect(() => {
    if (open) {
      closingRef.current = false;
      previouslyFocusedRef.current = isHTMLElement(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null;
    }
  }, [open]);

  const transformNotification = (notification: ApiNotification): UINotification => {
    const type = (['info', 'warning', 'success', 'error'].includes(notification.type?.toLowerCase() || '')
      ? notification.type?.toLowerCase()
      : 'info') as 'info' | 'warning' | 'success' | 'error';
    return {
      id: String(notification.id),
      title: notification.title || 'Notification',
      message: notification.message,
      read: Boolean(notification.read),
      type,
      createdAt: notification.createdAt ? new Date(notification.createdAt).toISOString() : new Date().toISOString(),
      link: notification.link,
      metadata: notification.metadata,
      expanded: false,
    };
  };

  const fetchNotifications = useCallback(async () => {
    if (!open) return;
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.notifications.getSummary();
      if (!isMounted.current) return;

      let items: ApiNotification[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response && typeof response === 'object') {
        const obj = response as Record<string, unknown>;
        if (Array.isArray((obj as { items?: unknown }).items)) {
          items = (obj as { items: ApiNotification[] }).items;
        } else if (Array.isArray((obj as { data?: unknown }).data)) {
          items = (obj as { data: ApiNotification[] }).data;
        }
      }
      setNotifications(items.map(transformNotification));
    } catch (e) {
      console.error('Error fetching notifications:', e);
      if (isMounted.current) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        setError(err?.response?.data?.message || err?.message || 'Failed to load notifications. Please try again.');
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [open]);

  const handleMarkAsRead = useCallback(async (id: string, currentStatus: boolean, event?: React.MouseEvent) => {
    event?.stopPropagation();
    const newStatus = !currentStatus;

    try {
      // Optimista
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: newStatus } : n));
      const response = newStatus ? await api.notifications.markAsRead(id) : await api.notifications.markAsUnread(id);
      if (response && typeof response === 'object' && 'id' in response) {
        setNotifications(prev => prev.map(n => n.id === id ? transformNotification(response as ApiNotification) : n));
      }
    } catch (e) {
      console.error('Error updating notification status:', e);
      if (isMounted.current) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: currentStatus } : n));
      }
    }
  }, []);

  useEffect(() => { if (open) { void fetchNotifications(); } }, [open, fetchNotifications]);

  const markAllAsRead = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (unreadCount === 0) return;
    const previous = notifications;
    try {
      setIsUpdating(true);
      setNotifications(prev => prev.map(n => (n.read ? n : { ...n, read: true })));
      await api.notifications.markAllAsRead();
      setError(null);
      await fetchNotifications();
    } catch (e) {
      console.error('Error marking all as read:', e);
      setNotifications(previous);
      setError('Failed to mark all notifications as read');
    } finally {
      if (isMounted.current) setIsUpdating(false);
    }
  }, [fetchNotifications, notifications, unreadCount]);

  // Mover el foco fuera del modal ANTES de cerrar (evita el warning de aria-hidden)
  const moveFocusOut = useCallback(() => {
    const active = document.activeElement;
    if (isHTMLElement(active)) {
      active.blur(); // esto mueve el foco al body en la mayoría de navegadores
    }
    // Devuelve el foco al elemento que lo tenía antes de abrir, si existe y sigue en el documento
    const prev = previouslyFocusedRef.current;
    if (prev && document.contains(prev)) {
      try { prev.focus(); } catch { /* noop */ }
    }
  }, []);

  // Cierre seguro (un solo click), sin any y moviendo el foco fuera
  const handleClose = useCallback((event?: unknown) => {
    if (closingRef.current) return;
    if (isUpdating) return;

    if (hasStopPropagation(event)) event.stopPropagation();

    moveFocusOut();           // <- clave para evitar el warning
    closingRef.current = true;
    onClose();
  }, [isUpdating, moveFocusOut, onClose]);

  // MUI Dialog close handler with proper typing
  const handleDialogClose = useCallback((event: React.SyntheticEvent, _reason: 'backdropClick' | 'escapeKeyDown') => { // eslint-disable-line @typescript-eslint/no-unused-vars
    // si estamos actualizando, no cerramos
    if (isUpdating) return;

    // si quieres bloquear cierre por backdrop/escape, puedes condicionar aquí con _reason
    handleClose(event);
  }, [isUpdating, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isUpdating}
      disableScrollLock
      disableRestoreFocus // nosotros gestionamos el foco manualmente
      aria-labelledby={titleId}
    >
      <DialogTitle
        id={titleId}
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          pr: 1.5,
        }}
      >
        <Typography variant="h6" component="span">
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {unreadCount > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllAsRead} disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Mark all as read'}
            </Button>
          )}
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchNotifications}
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          {/* X de cierre: un solo click */}
          <IconButton onClick={handleClose} size="small" edge="end" aria-label="Close notifications">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {error && (
          <Box p={2} bgcolor="error.light" color="error.contrastText">
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {isLoading ? (
          <Box flex={1} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={3}
            textAlign="center"
          >
            <InfoOutlinedIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              No notifications to display
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              New notifications will appear here
            </Typography>
          </Box>
        ) : (
          <List
            sx={{
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: theme.palette.action.hover },
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.divider, borderRadius: '3px' },
            }}
          >
            {notifications.map((notification) => {
              const IconEl = notificationIcons[notification.type];
              const notificationDate = notification.createdAt ? formatDate(notification.createdAt) : '';
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    dense
                    sx={{
                      backgroundColor: notification.read ? 'transparent' : 'action.hover',
                      '&:hover': { backgroundColor: 'action.selected' },
                      transition: 'background-color 0.2s',
                      pr: 1,
                    }}
                    secondaryAction={
                      <Tooltip title={notification.read ? 'Mark as unread' : 'Mark as read'}>
                        <span>
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleMarkAsRead(notification.id, notification.read, e);
                            }}
                            size="small"
                            color={notification.read ? 'default' : 'primary'}
                          >
                            {notification.read ? <MarkAsUnreadIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40, mr: 1 }}>
                      <Box sx={{ color: `${notification.type}.main` }}>{IconEl}</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          color={notification.read ? 'textSecondary' : 'textPrimary'}
                          sx={{ textDecoration: notification.read ? 'line-through' : 'none', opacity: notification.read ? 0.8 : 1 }}
                        >
                          {notification.message}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          {notificationDate}
                        </Typography>
                      }
                      sx={{ my: 0.5 }}
                    />
                  </ListItem>
                  <Divider component="li" variant="inset" sx={{ mx: 2 }} />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1.5, justifyContent: 'space-between' }}
      >
        <Button
          size="small"
          onClick={handleClose}
          startIcon={<CloseIcon fontSize="small" />}
          disabled={isUpdating}
        >
          Close
        </Button>
        <Box>
          <Button
            size="small"
            onClick={fetchNotifications}
            disabled={isLoading}
            startIcon={<RefreshIcon fontSize="small" />}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllAsRead}
              disabled={isUpdating || unreadCount === 0}
              startIcon={<DoneAllIcon fontSize="small" />}
              color="primary"
            >
              Mark all as read
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationsModal;
